import { UserOption } from '@roc/feature-communication';
import { TaskV2, Task, TaskStatus, TaskStatusDB } from '../types/generalTasksTypes';
import { Document } from '@roc/feature-types';
import { TaskWorkflowStatus } from './generalTasksConstants';

export const createEmptyTask = (): Task => {
  return {
    attributes: {},
    assignees: [],
    comments: [],
    attachments: [],
    subtasks: [],
  } as Task;
};

export const createEmptyTaskV2 = (): TaskV2 => {
  return {
    taskId: undefined,
    title: '',
    description: '',
    status: undefined,
    dueDate: undefined,
    priority: undefined,
    ownerId: undefined,
    loanId: undefined,
    isSystemTask: false,
    category: '',
    taskAssignees: [],
    taskTags: [],
    taskWatchers: [],
    escalated: false,
    ownerName: '',
    assignedName: '',
    parentTaskId: undefined,
    observers: [],
    isInternal: false,
    timeElapsed: 0,
    type1: undefined,
    typeId1: undefined,
    type2: undefined,
    typeId2: undefined,
    information: '',
    templateId: undefined,
    taskCode: undefined,
    statusUpdateReason: '',
  };
};

export const cloneObject = obj => {
  return JSON.parse(JSON.stringify(obj));
};


export function mapLoanActorsToUserOptions(api: any): UserOption[] {
  const mappings = [
    { id: 'underwriterId', name: 'underwriterFullName', role: 'underwriter', roleDisplayName: 'Underwriter' },
    { id: 'insuranceSpecialistId', name: 'insuranceSpecialistFullName', role: 'insuranceSpecialist', roleDisplayName: 'Insurance Specialist' },
    { id: 'seniorUnderwriterId', name: 'seniorUnderwriterFullName', role: 'seniorUnderwriter', roleDisplayName: 'Senior Underwriter' },
    { id: 'appraisalSpecialistId', name: 'appraisalSpecialistFullName', role: 'appraisalSpecialist', roleDisplayName: 'Appraisal Specialist' },
    { id: 'creditSpecialistId', name: 'creditSpecialistFullName', role: 'creditSpecialist', roleDisplayName: 'Credit Specialist' },
    { id: 'backgroundSpecialistId', name: 'backgroundSpecialistFullName', role: 'backgroundSpecialist', roleDisplayName: 'Background Specialist' },
    { id: 'drawAdminSpecialistId', name: 'drawAdminSpecialistFullName', role: 'drawAdminSpecialist', roleDisplayName: 'Draw Admin Specialist' },
    { id: 'drawReviewSpecialistId', name: 'drawReviewSpecialistFullName', role: 'drawReviewSpecialist', roleDisplayName: 'Draw Review Specialist' },
    { id: 'payoffSpecialistId', name: 'payoffSpecialistFullName', role: 'payoffSpecialist', roleDisplayName: 'Payoff Specialist' },
    { id: 'extensionSpecialistId', name: 'extensionSpecialistFullName', role: 'extensionSpecialist', roleDisplayName: 'Extension Specialist' },
    { id: 'postClosingSpecialistId', name: 'postClosingSpecialistFullName', role: 'postClosingSpecialist', roleDisplayName: 'Post Closing Specialist' },
    { id: 'mersSpecialistId', name: 'mersSpecialistFullName', role: 'mersSpecialist', roleDisplayName: 'MERS Specialist' },
    { id: 'qcSpecialistId', name: 'qcSpecialistFullName', role: 'qcSpecialist', roleDisplayName: 'QC Specialist' },
    { id: 'wiresSpecialistId', name: 'wiresSpecialistFullName', role: 'wiresSpecialist', roleDisplayName: 'Wires Specialist' },
    { id: 'originationSupportId', name: 'originationSupportFullName', role: 'originationSupport', roleDisplayName: 'Origination Support' },
    { id: 'tamariskSpecialistId', name: 'tamariskSpecialistFullName', role: 'tamariskSpecialist', roleDisplayName: 'Tamarisk Specialist' },
    { id: 'creditBackgroundSpecialistId', name: 'creditBackgroundSpecialistFullName', role: 'creditBackgroundSpecialist', roleDisplayName: 'Credit Background Specialist' },
    { id: 'postMortemUnderwriterId', name: 'postMortemSpecialistFullName', role: 'postMortemUnderwriter', roleDisplayName: 'Post Mortem Underwriter' },
    { id: 'valuationReviewerId', name: 'valuationReviewerFullName', role: 'valuationReviewer', roleDisplayName: 'Valuation Reviewer' }
  ];

  return mappings
    .map(({ id, name, role, roleDisplayName }) => {
      const userId = api[id];
      const fullName = api[name];
      if (!userId || !fullName || fullName === 'N/A') return null;
      const [firstName = '', ...lastParts] = fullName.split(' ');
      const lastName = lastParts.join(' ');
      return {
        firstName,
        lastName,
        emailAddress: null,
        userId,
        role,
        roleDisplayName,
        roleDisplayColor: null,
        active: true,
        isRoleInternal: true,
      };
    })
    .filter(Boolean);
}

export const areActionsUnavailableBasedOnDocument = (document: Document) => {
  return areActionsUnavailableBasedOnTaskQueueStatus(document?.taskQueueStatus);
};

export const areActionsUnavailableBasedOnTask = (task: TaskV2) => {
  return areActionsUnavailableBasedOnTaskQueueStatus(task?.taskQueueStatus);
};

export const areActionsUnavailableBasedOnTaskQueueStatus = (taskQueueStatus: String) => {
  const actionsUnavailable = taskQueueStatus === TaskWorkflowStatus.IN_PROGRESS;

  return actionsUnavailable;
};

export const isTaskAccepted = (status?: string) => {
  return status === TaskStatusDB.ACCEPTED || status === TaskStatus.ACCEPTED;
};

export function parseTaskStatusToDB(status: TaskStatus): TaskStatusDB {
  const map: Record<TaskStatus, TaskStatusDB> = {
    [TaskStatus.PENDING]: TaskStatusDB.PENDING,
    [TaskStatus.MORE_INFO_NEEDED]: TaskStatusDB.MORE_INFO_NEEDED,
    [TaskStatus.BEING_REVIEWED]: TaskStatusDB.BEING_REVIEWED,
    [TaskStatus.ACCEPTED]: TaskStatusDB.ACCEPTED,
    [TaskStatus.NOT_APPLICABLE]: TaskStatusDB.NOT_APPLICABLE,
    [TaskStatus.INACTIVE]: TaskStatusDB.INACTIVE,
  };

  return map[status];
}

export function parseTaskStatusDBToStatus(status: TaskStatusDB): TaskStatus {
  const map: Record<TaskStatusDB, TaskStatus> = {
    [TaskStatusDB.PENDING]: TaskStatus.PENDING,
    [TaskStatusDB.MORE_INFO_NEEDED]: TaskStatus.MORE_INFO_NEEDED,
    [TaskStatusDB.BEING_REVIEWED]: TaskStatus.BEING_REVIEWED,
    [TaskStatusDB.ACCEPTED]: TaskStatus.ACCEPTED,
    [TaskStatusDB.NOT_APPLICABLE]: TaskStatus.NOT_APPLICABLE,
    [TaskStatusDB.INACTIVE]: TaskStatus.INACTIVE,
  };

  return map[status];
}

export function getTaskStatusToDBList(): TaskStatusDB[] {
  const list: TaskStatusDB[] = [
    TaskStatusDB.PENDING,
    TaskStatusDB.MORE_INFO_NEEDED,
    TaskStatusDB.BEING_REVIEWED,
    TaskStatusDB.ACCEPTED,
    TaskStatusDB.NOT_APPLICABLE,
    TaskStatusDB.INACTIVE,
  ];

  return list;
}
